import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sun,
  FolderKanban,
  Megaphone,
  Wand2,
  Calculator,
  CalendarDays,
  Banknote,
  Settings,
  BookOpen,
  ArrowRight,
  CheckCircle2,
  Circle,
  PartyPopper,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/components/layout/AppShell";

const ALL_STEPS = [
  { key: "today", label: "Start your day on Today", desc: "Your dashboard \u2014 what needs attention, live jobs and cash at a glance.", to: "/", icon: Sun },
  { key: "jobs", label: "Open a job", desc: "Everything hangs off the job \u2014 plans, costs, schedule and client updates.", to: "/jobs", icon: FolderKanban, perm: "jobs" },
  { key: "crm", label: "Capture a lead", desc: "Track enquiries and follow-ups in the pipeline.", to: "/crm", icon: Megaphone, perm: "crm" },
  { key: "quick_quote", label: "Draft a Quick Quote", desc: "AI-assisted pricing for early enquiries.", to: "/quick-quote", icon: Wand2, perm: "quick_quote" },
  { key: "boq", label: "Run an AI takeoff", desc: "Upload a floor plan and let the AI measure it.", to: "/boq", icon: Calculator, perm: "boq" },
  { key: "schedule", label: "Check the schedule", desc: "Gantt charts, bookings and the trades on site today.", to: "/schedule", icon: CalendarDays, perm: "schedule" },
  { key: "cashflow", label: "Watch the cash", desc: "Projections, stage funding needs and low-cash warnings.", to: "/cashflow", icon: Banknote, perm: "cashflow" },
  { key: "settings", label: "Set up team & Xero", desc: "Invite your team, tune cost centres and connect Xero.", to: "/settings", icon: Settings, adminOnly: true },
  { key: "help", label: "Know where Help lives", desc: "The manual and printable guides, one click away.", to: "/help", icon: BookOpen },
];

const CONTRACTOR_STEPS = [
  { key: "today", label: "Start your day on Today", desc: "Your bookings and assigned work at a glance.", to: "/", icon: Sun },
  { key: "jobs", label: "Open your assigned jobs", desc: "Plans, schedules and documents shared with you.", to: "/jobs", icon: FolderKanban },
  { key: "help", label: "Know where Help lives", desc: "Guides for everything you can see.", to: "/help", icon: BookOpen },
];

export const OnboardingChecklist = () => {
  const { user, updateUser, isReadOnly } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Phase 191 — the tour is about *doing* things (quotes, takeoffs, leads) and saves progress; not for view-only accounts
  const dismissed = !!user?.onboarding?.dismissed || !!isReadOnly;
  const doneSteps = useMemo(() => user?.onboarding?.steps || {}, [user]);

  const steps = useMemo(() => {
    if (!user) return [];
    if (user.role === "contractor") return CONTRACTOR_STEPS;
    return ALL_STEPS.filter((s) => {
      if (s.adminOnly) return user.role === "admin";
      return hasPerm(user, s.perm);
    }).slice(0, 7);
  }, [user]);

  useEffect(() => {
    if (user && !dismissed) setOpen(true);
  }, [user, dismissed]);

  if (!user || steps.length === 0) return null;

  const doneCount = steps.filter((s) => doneSteps[s.key]).length;
  const allDone = doneCount === steps.length;
  const progress = Math.round((doneCount / steps.length) * 100);

  const persist = async (payload) => {
    try {
      const res = await api.post("/users/me/onboarding", payload);
      updateUser({ onboarding: res.data });
    } catch {
      /* silent \u2014 progress is a nicety, never block the user */
    }
  };

  const markStep = (key) => {
    if (!doneSteps[key]) persist({ steps: { [key]: true } });
  };

  const go = (step) => {
    markStep(step.key);
    setOpen(false);
    navigate(step.to);
  };

  const dismiss = () => {
    setOpen(false);
    persist({ dismissed: true });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : dismiss())}>
      <DialogContent className="sm:max-w-md" data-testid="onboarding-checklist-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {allDone ? (
              <span className="flex items-center gap-2">
                <PartyPopper className="h-5 w-5 text-primary" /> You're all set!
              </span>
            ) : (
              `Welcome, ${(user.name || "").split(" ")[0]}`
            )}
          </DialogTitle>
          <DialogDescription>
            {allDone
              ? "You've explored everything on your checklist. Find this guide again anytime under Help."
              : "A quick tour of what your account can do. Tick things off as you explore \u2014 your progress is saved."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <Progress value={progress} className="h-2 flex-1" data-testid="onboarding-progress-bar" />
          <span className="text-xs font-medium tabular-nums text-muted-foreground" data-testid="onboarding-progress-label">
            {doneCount}/{steps.length}
          </span>
        </div>

        <div className="max-h-[46vh] space-y-1 overflow-y-auto pr-1">
          {steps.map((step) => {
            const done = !!doneSteps[step.key];
            const Icon = step.icon;
            return (
              <button
                key={step.key}
                onClick={() => go(step)}
                data-testid={`onboarding-step-${step.key}`}
                className={`group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors duration-150 ${
                  done
                    ? "border-primary/30 bg-primary/5"
                    : "border-border hover:border-primary/40 hover:bg-muted/50"
                }`}
              >
                {done ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
                ) : (
                  <Circle className="h-5 w-5 shrink-0 text-muted-foreground/50" />
                )}
                <span className="min-w-0 flex-1">
                  <span className={`block text-sm font-medium ${done ? "text-muted-foreground line-through" : ""}`}>
                    {step.label}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">{step.desc}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                  <Icon className="h-3.5 w-3.5" /> Go <ArrowRight className="h-3 w-3" />
                </span>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            variant={allDone ? "default" : "ghost"}
            onClick={dismiss}
            data-testid="onboarding-dismiss-button"
          >
            {allDone ? "Finish" : "Skip for now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
